import React, { useContext, useState, useEffect, useRef } from 'react';
import { Input, InputGroup, Button, Label } from 'reactstrap';
import { DepartmentContext } from '../../../../../DepartmentWrapper';
import { postDataAgnostic } from '../../../../../Services/dataApi';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';

import style from './style';
import { checkLoginForError, formatBearerToken } from '../../../../../Common.functions';
import { useAuth0 } from '../../../../../auth0/reactAuth0Spa';
import Swal from 'sweetalert2';
import { clone } from 'lodash';

const AddTimeSlotPopover = ({
  pk_InterviewDate,
  forcedContext,
  row,
  fetchSchedule,
  hideHeaderPopover,
  scheduleData,
  setShowAddTimeSlotPopover,
  onChangeDetected,
  onScheduleTimeShift,
  isOpen,
}) => {
  const [slotsToAdd, setSlotsToAdd] = useState(1);
  const [originalSlotsToAdd, setOriginalSlotsToAdd] = useState(1);
  const slotsToAddInputRef = useRef(null);
  const deptContext = useContext(DepartmentContext);
  const dContext = forcedContext ? forcedContext : deptContext;
  const history = useHistory();
  const { getTokenSilently, loginWithRedirect } = useAuth0();

  // Initialize original value
  useEffect(() => {
    setOriginalSlotsToAdd(1);
  }, []);

  // Detect changes
  useEffect(() => {
    const hasChanges = slotsToAdd !== originalSlotsToAdd;
    if (onChangeDetected) {
      onChangeDetected(hasChanges);
    }
  }, [slotsToAdd, originalSlotsToAdd, onChangeDetected]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const timer = setTimeout(() => {
      slotsToAddInputRef.current && slotsToAddInputRef.current.focus();
    }, 0);

    return () => clearTimeout(timer);
  }, [isOpen]);

  const insertTimeslot = (mode) => {
    if (slotsToAdd === '' || slotsToAdd < 1) {
      toast.error('Please choose a number between 1 and 50');
      return;
    }
    // Reset change flag before saving
    if (onChangeDetected) {
      onChangeDetected(false);
    }
    return getTokenSilently()
      .then((token) => {
        const rowSortOrders = [];
        const pathExploded = history.location.pathname.split('/');
        const scheduleID = pk_InterviewDate ? pk_InterviewDate : pathExploded[pathExploded.length - 1];

        let toAdd = slotsToAdd;

        if (row) {
          const { SortOrder } = row;
          // Inserting in the middle of schedule.
          let currentSlot = SortOrder;
          if (mode === 'after') {
            currentSlot = SortOrder + 1;
          }

          while (toAdd > 0) {
            rowSortOrders.push(currentSlot);
            toAdd--;
            currentSlot++;
          }
        } else {
          // Inserting at beginning or end of schedule.
          let highestSortOrder = null;
          let lowestSortOrder = null;
          scheduleData.body.forEach((item) => {
            if (item.pk_Timeslot === 'END') {
              return;
            }
            if (highestSortOrder == null || item.SortOrder > highestSortOrder) {
              highestSortOrder = item.SortOrder;
            }
            if (lowestSortOrder == null || item.SortOrder < lowestSortOrder) {
              lowestSortOrder = item.SortOrder;
            }
          });

          let currentSlot = 0;
          if (mode === 'after') {
            currentSlot = highestSortOrder + 1;
          }

          while (toAdd > 0) {
            rowSortOrders.push(currentSlot);
            toAdd--;
            currentSlot++;
          }
        }

        postDataAgnostic(
          'department/season/schedule/row',
          {
            pk_Department: dContext.department.pk_Department,
            pk_InterviewDate: Number(scheduleID),
          },
          {
            rowSortOrders: rowSortOrders,
          },
          formatBearerToken(token),
        )
          .then(async (res) => {
            const { metaData = {}, body = [] } = scheduleData || {};
            const standardDuration = parseInt(metaData.StandardDurationInMinutes) || 0;
            const standardPassing = parseInt(metaData.StandardPassingDurationInMinutes) || 0;
            const slotUnitInMinutes = standardDuration + standardPassing;
            const slotsCount = parseInt(slotsToAdd) || 0;
            const deltaMinutes = slotUnitInMinutes * slotsCount;

            const nonEndRows = (body || []).filter((item) => item && item.pk_Timeslot !== 'END');
            let anchorDateTime = null;

            if (row) {
              anchorDateTime = mode === 'before' ? row.slot : row.slotEnd;
            } else if (nonEndRows.length > 0) {
              const sortedRows = [...nonEndRows].sort((a, b) => (a.SortOrder > b.SortOrder ? 1 : -1));
              anchorDateTime = mode === 'before' ? sortedRows[0].slot : sortedRows[sortedRows.length - 1].slotEnd;
            }

            console.log('[Schedule Debug] AddTimeSlotPopover insert success', {
              mode,
              slotsToAdd,
              slotUnitInMinutes,
              deltaMinutes,
              anchorDateTime,
              hasScheduleData: !!scheduleData,
              flexEventCount: metaData && metaData.FlexEvents ? metaData.FlexEvents.length : 0,
              rowPk: row ? row.pk_Timeslot : null,
            });

            if (onScheduleTimeShift && deltaMinutes > 0 && anchorDateTime) {
              console.log('[Schedule Debug] AddTimeSlotPopover calling onScheduleTimeShift', {
                deltaMinutes,
                anchorDateTime,
              });
              await onScheduleTimeShift({
                deltaMinutes,
                anchorDateTime,
              });
            } else {
              console.log('[Schedule Debug] AddTimeSlotPopover NOT calling onScheduleTimeShift', {
                hasOnScheduleTimeShift: !!onScheduleTimeShift,
                deltaMinutes,
                anchorDateTime,
              });
            }

            fetchSchedule(token);
            setShowAddTimeSlotPopover(false);
            hideHeaderPopover();
          })
          .catch((err) => {});
      })
      .catch((err) => {
        checkLoginForError({ err, loginWithRedirect });
      });
  };

  return (
    <div style={style.mainBody}>
      <Label style={style.inputLabel} for="slotsToAdd">
        Number of slots to add:
      </Label>
      <div style={style.simpleRow}>
        <Input
          innerRef={slotsToAddInputRef}
          type="number"
          style={style.inputFields}
          name="slotsToAdd"
          id="slotsToAdd"
          placeholder=""
          value={slotsToAdd}
          onChange={(e) => {
            let newValue = Number(e.target.value);

            // should let the user put in a blank value so they can type in a new number
            if (e.target.value === '') {
              setSlotsToAdd(null);
            } else if (newValue < 1) {
              setSlotsToAdd(1);
              toast.error('Timeslot insert cannot be less than 1');
            } else if (newValue > 50) {
              setSlotsToAdd(50);
              toast.error('Timeslot insert cannot exceed 50');
            } else {
              setSlotsToAdd(newValue);
            }
          }}
        />
      </div>

      <div style={style.simpleRow}>
        <Button
          color="danger"
          style={{ ...style.genButton, backgroundColor: null }}
          onClick={() => {
            setShowAddTimeSlotPopover(false);
          }}
        >
          Cancel
        </Button>
        <Button
          style={style.genButton}
          onClick={(e) => {
            e.preventDefault();
            insertTimeslot('before');
          }}
        >
          {row ? 'Before' : 'Beginning'}
        </Button>
        <Button
          style={style.genButton}
          onClick={(e) => {
            e.preventDefault();
            insertTimeslot('after');
          }}
        >
          {row ? 'After' : 'End'}
        </Button>
      </div>
      <div style={style.simpleRow}></div>
      <div style={style.simpleRow}></div>
    </div>
  );
};

export default AddTimeSlotPopover;
